/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';

interface GTMConfig {
  gtmId: string;
  dataLayer?: Record<string, unknown>;
  dataLayerName?: string;
  auth?: string;
  preview?: string;
}

interface UseGoogleTagManagerReturn {
  isInitialized: boolean;
  gtmId: string | null;
}

/**
 * Hook to initialize Google Tag Manager
 * Uses VITE_GTM_ID environment variable
 * Automatically initializes GTM on mount if GTM ID is available
 */
export const useGoogleTagManager = (): UseGoogleTagManagerReturn => {
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Get GTM ID from environment variable (supports both import.meta.env and window.ENV)
  const gtmId = import.meta.env.VITE_GTM_ID || (window as any).ENV?.VITE_GTM_ID || null;

  useEffect(() => {
    // Only initialize if GTM ID is available and not already initialized
    if (!gtmId || isInitialized) {
      return;
    }

    try {
      const tagManagerArgs: GTMConfig = {
        gtmId,
      };

      TagManager.initialize(tagManagerArgs);
      setIsInitialized(true);
    } catch (error) {
      // Silent fail - GTM initialization errors should not break the app
      setIsInitialized(false);
    }
  }, [gtmId, isInitialized]);

  return {
    isInitialized,
    gtmId,
  };
};

/**
 * Push custom events to GTM dataLayer
 * @param event - Event name
 * @param data - Additional event data
 */
export const pushToDataLayer = (event: string, data?: Record<string, unknown>): void => {
  try {
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event,
        ...data,
      });
    }
  } catch (error) {
    // Silent fail - dataLayer push errors should not break the app
  }
};
